import React from 'react';
import { Phone, ExternalLink } from 'lucide-react';
import { useNation } from '../i18n/NationContext';

/* Always-visible quick-access helplines for SV and DA guide pages.
 * variant: 'sv' shows Rape Crisis as the specialist line, 'da' shows
 * National Domestic Abuse Helpline (Refuge). 999 and Samaritans appear
 * on both. When the visitor has selected Scotland, we swap in the
 * Scottish specialist line for that topic. Kept small so it never
 * dominates the page. */
const COMMON = [
  {
    id: '999',
    label: '999',
    sub: 'Emergency',
    tel: '999',
    tone: 'urgent',
  },
  {
    id: 'samaritans',
    label: 'Samaritans',
    sub: '116 123 · 24/7',
    tel: '116123',
    tone: 'calm',
  },
];

const SPECIALIST = {
  sv: {
    ew: {
      id: 'rape-crisis',
      label: 'Rape Crisis',
      sub: '0808 802 9999 · 24/7',
      tel: '08088029999',
      tone: 'purple',
    },
    scotland: {
      id: 'rape-crisis-scotland',
      label: 'Rape Crisis Scotland',
      sub: '08088 01 03 02 · 5pm–midnight',
      tel: '08088010302',
      tone: 'purple',
    },
  },
  da: {
    ew: {
      id: 'refuge',
      label: 'Refuge DV line',
      sub: '0808 2000 247 · 24/7',
      tel: '08082000247',
      tone: 'purple',
    },
    scotland: {
      id: 'sdafmh',
      label: "Scotland DA line",
      sub: '0800 027 1234 · 24/7',
      tel: '08000271234',
      tone: 'purple',
    },
  },
};

const TONE_STYLES = {
  urgent: {
    background: 'var(--tf-purple)',
    color: '#fff',
    border: '1px solid var(--tf-purple)',
  },
  calm: {
    background: '#fff',
    color: 'var(--tf-text)',
    border: '1px solid var(--tf-card-border)',
  },
  purple: {
    background: 'var(--tf-bg-secondary)',
    color: 'var(--tf-text)',
    border: '1px solid var(--tf-purple-light)',
  },
};

export default function HelplinesStrip({ variant = 'sv' }) {
  const { nation } = useNation();
  const specialist = SPECIALIST[variant]?.[nation] || SPECIALIST[variant].ew;
  const items = [COMMON[0], specialist, COMMON[1]];

  return (
    <section
      data-testid={`helplines-strip-${variant}`}
      className="tf-no-print rounded-2xl p-3 sm:p-4 mt-6"
      style={{
        background: '#fff',
        border: '1px solid var(--tf-purple-light)',
      }}
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <p
          className="font-body font-bold"
          style={{
            fontSize: 10.5,
            letterSpacing: '0.22em',
            color: 'var(--tf-purple)',
          }}
        >
          QUICK HELP · TAP TO CALL
        </p>
        <a
          href={`/app/support?tab=${variant}`}
          className="inline-flex items-center gap-1 font-bold"
          data-testid={`helplines-strip-${variant}-all`}
          style={{
            fontSize: 11.5,
            color: 'var(--tf-purple)',
            textDecoration: 'none',
          }}
        >
          All helplines
          <ExternalLink size={12} />
        </a>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {items.map((h) => {
          const style = TONE_STYLES[h.tone];
          return (
            <a
              key={h.id}
              href={`tel:${h.tel}`}
              data-testid={`helplines-strip-${variant}-${h.id}`}
              className="flex items-center gap-2 sm:gap-3 px-3 py-2.5 rounded-xl font-bold transition-transform active:scale-[0.98]"
              style={{ ...style, textDecoration: 'none' }}
            >
              <Phone
                size={15}
                strokeWidth={2.2}
                style={{
                  flexShrink: 0,
                  opacity: h.tone === 'urgent' ? 1 : 0.85,
                }}
              />
              <span className="flex flex-col leading-tight min-w-0">
                <span
                  className="font-body"
                  style={{ fontSize: 12.5, fontWeight: 700 }}
                >
                  {h.label}
                </span>
                <span
                  className="font-body truncate"
                  style={{
                    fontSize: 10.5,
                    opacity: h.tone === 'urgent' ? 0.92 : 0.7,
                    fontWeight: 600,
                  }}
                >
                  {h.sub}
                </span>
              </span>
            </a>
          );
        })}
      </div>
    </section>
  );
}
